import React, { useCallback } from 'react';
import { queryCache, useMutation, useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router';
import Skeleton from 'react-loading-skeleton';
import { Box } from '@chakra-ui/react';
import { css } from '@emotion/react';
import { colors } from '@atlaskit/theme';
import { e100 } from '@atlaskit/theme/elevation';
import { Grid, GridColumn } from '@atlaskit/page';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import PageHeader from '@atlaskit/page-header';
import CustomerForm from './CustomerForm';
import { Customer, SaleItem } from './types';
import api from '../../libs/api';
import { FormattedMessage } from '../../components/i18n';
import { useNotifications } from '../../components/notification';
import { useConfirm } from '../../components/modal-dialog';

const saveCustomer = async (customer: Customer) => api.put('/Customers', customer);
const deleteCustomer = async (id: string) =>
  api.delete(`/customers/${id}`);

type Props = {
  isNew?: boolean;
};
const CustomerEditPage: React.FC<Props> = ({ isNew }) => {
  const { showSuccessNotification, showErrorNotification } = useNotifications();

  const fetchCustomerById = React.useCallback(async (type, id) => api.get(`/customers/${id}`), []);
  const { confirmDelete } = useConfirm();

  const { id } = useParams();
  const {data, error, isLoading } = useQuery(['customers', id], fetchCustomerById, {enabled: !isNew});
  let customer: Customer = data;

  const navigate = useNavigate();

  const handleDelete = useCallback(async (customer: Customer) => {
    confirmDelete({
      title: (
        <FormattedMessage
          id="sale.CustomerEditPage.modals.delete.title`"
          defaultMessage="Delete this customer?"
        />
      ),
      content: (
        <FormattedMessage
          id="sale.CustomerEditPage.modals.delete.content`"
          defaultMessage={'You are about to delete customer "{name}".'}
          values={{ name: customer.name }}
        />
      ),
      onConfirm: async () => {
        try {
          await deleteCustomer(customer.id!);
          showSuccessNotification(
            <FormattedMessage
              id="sale.CustomerEditPage.notifications.delete.success.title`"
              defaultMessage="Customer deleted"
            />
          );
          navigate('../');
        } catch (e) {
          showErrorNotification(e.message);
        }
        queryCache.invalidateQueries('customers');
      },
    });
  }, []);


  if (isNew) {
    customer = { isNew: true, type: 'business', address: {
      country: 'NL'
    } } as Customer;
  }

  // const customer = null;
  const [save, { status, isLoading: isSaving }] = useMutation(saveCustomer, {
    onSuccess: (savedCustomer: Customer) => {
      showSuccessNotification(<FormattedMessage
        id="sale.CustomerEditPage.notifications.updated.success.title`"
        defaultMessage="Customer updated"
      />);
      queryCache.invalidateQueries('customers');
      queryCache.invalidateQueries(['customer', savedCustomer.id]);
      if (isNew) {
        navigate(`../${savedCustomer.id}/edit`);
      }
    },
    onError: (e: any) => {
      showErrorNotification(
        e.message
      );
    }
  });

  const handleSave = useCallback(() => {
    // document.getElementById('customerForm')?.dispatchEvent(new Event('submit', { cancelable: true }))
    document.getElementById('customerForm')?.click();
  }, []);

  return (
    <Box ml={10} mt={-8} display="flex" flexDirection="column">
      {!isLoading? (
        <h1>
          {isNew? (
            <FormattedMessage
              id='sale.CustomerEditDrawer.title.new'
              defaultMessage='New customer'
            />
          ) : (
            <>
              {customer?.name}
              {customer?.customerNumber && ` (${customer?.customerNumber})`}
            </>
          )}
        </h1>
      ) :(
        <h1>
          <Skeleton
            count={1}
            height="31px"
            width="50%"
          />
        </h1>
      )}
      <Box
        overflowY="scroll"
        mt={4}
        borderRadius="md"
        w="600px"
        p={6}
        pt={4}
        css={css`
            flex: 1;
            overflow: auto;
            position: relative;
            background-color: ${colors.N10};
            ${e100}
        `}
      >
        <Grid>
          <GridColumn>
            <Box>
              <CustomerForm
                initialValues={customer}
                onSubmit={save}
              />
            </Box>
          </GridColumn>
        </Grid>
      </Box>
      <Box py={5} display='flex' justifyContent="flex-end">
        <ButtonGroup>
          <LoadingButton
            appearance="primary"
            isLoading={false}
            onClick={handleSave}
          >
            <FormattedMessage id="common.save" defaultMessage="Save" />
          </LoadingButton>
          <Button appearance="subtle" onClick={() => handleDelete(customer)}>
            <FormattedMessage id="common.delete" defaultMessage="Delete" />
          </Button>
        </ButtonGroup>
      </Box>
    </Box>
  );
};

export default CustomerEditPage;
